import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, CheckCircle2, XCircle, Copy as CopyIcon, Clock } from "lucide-react";
import { api, apiError } from "@/api";
import { DropZone } from "@/components/VariationsEditor";
import { sha256, shrinkIfHuge, uploadWithRetry } from "@/lib/uploadQueue";

const CONCURRENCY = 3;
const nameFromFile = (fn) => (fn || "sticker").replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim().slice(0, 60) || "sticker";

const STATUS = {
  waiting: { label: "Aguardando", cls: "text-[#7A7571]", Icon: Clock },
  sending: { label: "Enviando", cls: "text-[#B07A7F]", Icon: Loader2 },
  ok: { label: "Enviada", cls: "text-[#4F8A5B]", Icon: CheckCircle2 },
  duplicate: { label: "Repetida (pulada)", cls: "text-[#B08A3E]", Icon: CopyIcon },
  failed: { label: "Falhou", cls: "text-[#C0555A]", Icon: XCircle },
};

function Row({ item, idx }) {
  const s = STATUS[item.status];
  return (
    <li className="flex items-center gap-2 py-1.5 border-b border-[#F2E6E7] last:border-0" data-testid={`bulk-item-${idx}`} data-status={item.status}>
      <s.Icon size={15} className={`${s.cls} shrink-0 ${item.status === "sending" ? "animate-spin" : ""}`} />
      <span className="flex-1 min-w-0 truncate text-xs">{item.file.name}</span>
      <span className={`text-[11px] max-w-[45%] truncate ${s.cls}`} title={item.error || ""}>
        {item.status === "sending" ? (item.retry ? `nova tentativa ${item.retry}…` : `${item.progress}%`) : item.status === "failed" ? item.error || s.label : s.label}
      </span>
    </li>
  );
}

export function BulkUploader({ cats, packs, inputCls, btnPrimary, onDone, onBusyChange }) {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  const [pack, setPack] = useState("");
  const [busy, setBusy] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const stop = useRef(false);
  const list = useRef([]);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    const guard = (e) => { if (busy) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("beforeunload", guard);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); window.removeEventListener("beforeunload", guard); };
  }, [busy]);

  useEffect(() => { onBusyChange && onBusyChange(busy); }, [busy, onBusyChange]);

  const patch = (i, p) => {
    list.current[i] = { ...list.current[i], ...p };
    setItems([...list.current]);
  };

  const addFiles = (files) => {
    const known = new Set(list.current.map((x) => `${x.file.name}|${x.file.size}`));
    const fresh = files.filter((f) => !known.has(`${f.name}|${f.size}`)).map((file) => ({ file, status: "waiting", progress: 0 }));
    list.current = [...list.current, ...fresh];
    setItems([...list.current]);
  };

  const sendOne = async (i, seen) => {
    const { file } = list.current[i];
    patch(i, { status: "sending", progress: 0, retry: 0, error: "" });
    try {
      const hash = list.current[i].hash || (await sha256(file));
      patch(i, { hash });
      if (seen.has(hash)) { patch(i, { status: "duplicate" }); return 0; }
      seen.add(hash);
      const ready = await shrinkIfHuge(file);
      await uploadWithRetry(() => {
        const fd = new FormData();
        fd.append("name", nameFromFile(file.name));
        fd.append("keywords", "");
        fd.append("category_id", cat);
        fd.append("pack_id", pack);
        fd.append("is_new", "true");
        fd.append("notify", "false");
        fd.append("file_hash", hash);
        fd.append("file", ready, ready.name || file.name);
        return fd;
      }, (progress, retry) => patch(i, retry ? { progress, retry } : { progress }), () => stop.current);
      patch(i, { status: "ok", progress: 100 });
      return 1;
    } catch (e) {
      if (e?.response?.status === 409) { patch(i, { status: "duplicate" }); return 0; }
      patch(i, { status: "failed", error: e?.response ? apiError(e) : "Sem conexão — tente reenviar" });
      return 0;
    }
  };

  const run = async (onlyFailed) => {
    const queue = list.current.map((x, i) => i).filter((i) => (onlyFailed ? list.current[i].status === "failed" : list.current[i].status === "waiting"));
    if (!queue.length) { toast.error("Selecione as imagens primeiro."); return; }
    stop.current = false;
    setBusy(true);
    const seen = new Set(list.current.filter((x) => x.status === "ok" && x.hash).map((x) => x.hash));
    let ok = 0;
    const worker = async () => {
      while (queue.length && !stop.current) {
        const sent = await sendOne(queue.shift(), seen);
        ok += sent;
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    setBusy(false);
    if (ok > 0) {
      api.post("/admin/notify-new", { count: ok }).catch(() => {});
      onDone();
    }
    const failed = list.current.filter((x) => x.status === "failed").length;
    if (failed) toast(`${ok} enviadas, ${failed} falharam. Toque em "Reenviar as que falharam".`);
    else toast.success(`${ok} figurinha(s) enviadas! 💗`);
  };

  const count = (st) => items.filter((x) => x.status === st).length;
  const done = items.length - count("waiting") - count("sending");

  return (
    <div className="space-y-4">
      <p className="text-sm text-[#7A7571]">
        Escolha quantas imagens quiser (da galeria do iPhone ou do computador). Elas sobem de 3 em 3, com nova tentativa automática se a internet falhar. Repetidas são puladas.
      </p>
      <DropZone onFiles={addFiles} disabled={busy} testid="bulk-file-drop" label={items.length ? `${items.length} imagem(ns) na lista — toque para adicionar mais` : "Arraste o pack aqui ou toque para escolher (PNG transparente)"} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <select className={inputCls} data-testid="bulk-category" value={cat} disabled={busy} onChange={(e) => setCat(e.target.value)}>
          <option value="">Categoria (para todas)</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
        </select>
        <select className={inputCls} data-testid="bulk-pack" value={pack} disabled={busy} onChange={(e) => setPack(e.target.value)}>
          <option value="">Pack (para todas)</option>
          {packs.map((p) => <option key={p.id} value={p.id}>{p.emoji} {p.name}</option>)}
        </select>
      </div>
      {offline && <p className="text-xs text-[#C0555A] text-center" data-testid="bulk-offline">Sem internet — o envio continua sozinho quando a conexão voltar.</p>}
      {items.length > 0 && (
        <div className="space-y-2" data-testid="bulk-progress">
          <div className="h-2.5 rounded-full bg-[#F2E6E7] overflow-hidden">
            <div className="h-full bg-[#C58B90] transition-[width] duration-300" style={{ width: `${(done / items.length) * 100}%` }} />
          </div>
          <p className="text-xs text-[#7A7571] text-center" data-testid="bulk-summary">
            {done}/{items.length} · {count("ok")} enviadas{count("duplicate") ? ` · ${count("duplicate")} repetidas` : ""}{count("failed") ? ` · ${count("failed")} falharam` : ""}
          </p>
          <ul className="max-h-56 overflow-y-auto rounded-2xl border border-[#F2E6E7] px-3" data-testid="bulk-list">
            {items.map((it, i) => <Row key={`${it.file.name}-${i}`} item={it} idx={i} />)}
          </ul>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={() => run(false)} disabled={busy || !count("waiting")} className={`${btnPrimary} flex-1`} data-testid="bulk-submit">
          {busy && <Loader2 size={17} className="animate-spin" />} {busy ? "Enviando…" : `Enviar ${count("waiting") || ""} figurinha(s)`}
        </button>
        {!busy && count("failed") > 0 && (
          <button onClick={() => run(true)} data-testid="bulk-retry-failed" className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-full border border-[#C58B90] text-[#C58B90] text-sm font-semibold hover:bg-[#F7EBEC] transition-colors">
            <RotateCcw size={16} /> Reenviar as que falharam ({count("failed")})
          </button>
        )}
        {busy && (
          <button onClick={() => { stop.current = true; }} data-testid="bulk-stop" className="flex-1 h-12 rounded-full bg-[#F2E6E7] text-[#8A5F63] text-sm font-semibold">
            Parar depois das atuais
          </button>
        )}
      </div>
    </div>
  );
}
